import React, { useMemo, useState } from "react";
import styles from "./questionnaireSelectionModal.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import {
  useLazyGetTherapistQuestionnairesForAutoFillQuery,
  useAutoFillActionReportMutation,
} from "../../../services/api/user.api";
import {
  TTherapistQuestionnaireForAutoFill,
  TAutoFillActionReportResponse,
} from "../../../services/types/user.types";

interface QuestionnaireSelectionModalProps {
  onClose: () => void;
  onComplete: (
    questionnaireId: number,
    questionnaireCompaniesId: number,
    autoFilledData: TAutoFillActionReportResponse
  ) => void;
  meetingSummary: string;
  transcript?: string;
  /** When a valid number, only questionnaires with this companies id are shown. */
  questionnaireCompaniesId?: number;
}

type ModalStep = "selection" | "processing" | "error";

const QuestionnaireSelectionModal: React.FC<QuestionnaireSelectionModalProps> = ({
  onClose,
  onComplete,
  meetingSummary,
  transcript,
  questionnaireCompaniesId: filterQuestionnaireCompaniesId,
}) => {
  const [step, setStep] = useState<ModalStep>("selection");
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<TTherapistQuestionnaireForAutoFill | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [
    fetchQuestionnaires,
    { data: questionnaires, isLoading: isLoadingQuestionnaires, isError: isFetchError },
  ] = useLazyGetTherapistQuestionnairesForAutoFillQuery();

  const [autoFillReport, { isLoading: isAutoFilling }] =
    useAutoFillActionReportMutation();

  const filterByCompaniesId = useMemo(() => {
    if (
      typeof filterQuestionnaireCompaniesId !== "number" ||
      !Number.isFinite(filterQuestionnaireCompaniesId)
    ) {
      return undefined;
    }
    return filterQuestionnaireCompaniesId;
  }, [filterQuestionnaireCompaniesId]);

  const filteredQuestionnaires = useMemo(() => {
    if (!questionnaires) return [];
    if (filterByCompaniesId === undefined) return questionnaires;
    return questionnaires.filter(
      (q) => q.questionnaireCompaniesId === filterByCompaniesId
    );
  }, [questionnaires, filterByCompaniesId]);

  React.useEffect(() => {
    if (
      selectedQuestionnaire &&
      filteredQuestionnaires.length > 0 &&
      !filteredQuestionnaires.some(
        (q) =>
          q.questionnaireCompaniesId ===
          selectedQuestionnaire.questionnaireCompaniesId
      )
    ) {
      setSelectedQuestionnaire(null);
    }
  }, [filteredQuestionnaires, selectedQuestionnaire]);

  // Fetch questionnaires on mount
  React.useEffect(() => {
    fetchQuestionnaires();
  }, [fetchQuestionnaires]);

  const handleSelect = (questionnaire: TTherapistQuestionnaireForAutoFill) => {
    setSelectedQuestionnaire(questionnaire);
  };

  const handleContinue = async () => {
    if (!selectedQuestionnaire) return;

    setStep("processing");
    setErrorMessage("");

    try {
      const result = await autoFillReport({
        QuestionnaireId: selectedQuestionnaire.questionnaireId,
        MeetingSummary: meetingSummary,
        Transcript: transcript,
      }).unwrap();

      if (result.success) {
        onComplete(
          selectedQuestionnaire.questionnaireId,
          selectedQuestionnaire.questionnaireCompaniesId,
          result
        );
      } else {
        setErrorMessage(result.message || t("AUTO_FILL_ERROR") || "Auto-fill failed");
        setStep("error");
      }
    } catch (error: any) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          t("AUTO_FILL_ERROR") ||
          "An error occurred"
      );
      setStep("error");
    }
  };

  const handleRetry = () => {
    setStep("selection");
    setErrorMessage("");
  };

  // Loading questionnaires state
  if (isLoadingQuestionnaires) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>
            {t("LOADING_QUESTIONNAIRES") || "Loading questionnaires..."}
          </p>
        </div>
      </div>
    );
  }

  // Error fetching questionnaires
  if (isFetchError) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>
          {t("ERROR") || "Error"}
        </h3>
        <p className={styles.error}>
          {t("FAILED_TO_LOAD_QUESTIONNAIRES") || "Failed to load questionnaires"}
        </p>
        <div className={styles.buttons}>
          <button className={`${styles.button} ${styles.secondaryButton}`} onClick={onClose}>
            {t("CLOSE") || "Close"}
          </button>
        </div>
      </div>
    );
  }

  // Processing state
  if (step === "processing" || isAutoFilling) {
    return (
      <div className={styles.container}>
        <div className={styles.progressContainer}>
          <h3 className={styles.progressTitle}>
            {t("AUTO_FILLING_REPORT") || "Auto-filling report..."}
          </h3>
          <div className={styles.progressAnimation}>
            <div className={styles.progressRing} />
            <span className={styles.progressIcon}>✨</span>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressBarFill} />
          </div>
          <p className={styles.progressSubtext}>
            {t("AUTO_FILL_PROCESSING_MESSAGE") ||
              "AI is analyzing the meeting summary and filling the report..."}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === "error") {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>
          {t("ERROR") || "Error"}
        </h3>
        <p className={styles.error}>{errorMessage}</p>
        <div className={styles.buttons}>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={handleRetry}
          >
            {t("TRY_AGAIN") || "Try Again"}
          </button>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={onClose}
          >
            {t("CLOSE") || "Close"}
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!filteredQuestionnaires.length) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>
          {t("SELECT_QUESTIONNAIRE") || "Select Questionnaire"}
        </h3>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📋</span>
          <p>{t("NO_QUESTIONNAIRES_AVAILABLE") || "No questionnaires available"}</p>
        </div>
        <div className={styles.buttons}>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={onClose}
          >
            {t("CLOSE") || "Close"}
          </button>
        </div>
      </div>
    );
  }

  // Selection state
  return (
    <div className={styles.container}>
      <h3 className={styles.title}>
        {t("SELECT_QUESTIONNAIRE_FOR_AUTO_FILL") || "Select Questionnaire for Auto-Fill"}
      </h3>
      <p className={styles.subtitle}>
        {t("AUTO_FILL_DESCRIPTION") ||
          "Choose a questionnaire to auto-fill based on the meeting summary"}
      </p>

      <div className={styles.list}>
        {filteredQuestionnaires.map((q) => (
          <div
            key={`${q.questionnaireId}-${q.questionnaireCompaniesId}`}
            className={`${styles.listItem} ${
              selectedQuestionnaire?.questionnaireCompaniesId === q.questionnaireCompaniesId
                ? styles.listItem_selected
                : ""
            }`}
            onClick={() => handleSelect(q)}
          >
            <div className={styles.listItemContent}>
              <span className={styles.listItemName}>{q.name}</span>
              {q.typeName && (
                <span className={styles.listItemType}>{q.typeName}</span>
              )}
            </div>
            <div
              className={`${styles.radio} ${
                selectedQuestionnaire?.questionnaireCompaniesId === q.questionnaireCompaniesId
                  ? styles.radio_selected
                  : ""
              }`}
            />
          </div>
        ))}
      </div>

      <div className={styles.buttons}>
        <button
          className={`${styles.button} ${styles.secondaryButton}`}
          onClick={onClose}
        >
          {t("CANCEL") || "Cancel"}
        </button>
        <button
          className={`${styles.button} ${styles.primaryButton}`}
          onClick={handleContinue}
          disabled={!selectedQuestionnaire}
        >
          {t("CONTINUE") || "Continue"}
        </button>
      </div>
    </div>
  );
};

export default QuestionnaireSelectionModal;
